// 🔧 scoring_builder.js
// ✅ Purpose: Experience Factory scoring-configuration builder for LGV Legacy
// ✅ Features:
// - Reads optional scoring configuration from the registered industry manifest
// - Safely no-ops for Experiences that do not define scoring configuration
// - Creates scoring factors under /events/{eventId}/scoringFactors
// - Creates scored highlights under each factor's /highlights subcollection
// - Uses an atomic Firestore write batch for the scoring structure
// - Uses the trusted updateVotingSettings callable for governed voting settings
// - Supports flexible judged ballot scales through maxBallotPoints
// - Supports role-controlled results through resultsAudience
// - Keeps scoring setup separate from judged vote generation
// - Fully block-commented for clarity and future devs

import {
  collection,
  doc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";

import {
  httpsCallable,
} from "firebase/functions";

import {
  db,
  functions,
} from "../../firebase";

import {
  logger,
} from "../../utils/logger";

// =====================================================
// Scoring template validation
// =====================================================
// Purpose:
// - Fail clearly before persistence when a purpose-built
//   Experience contains malformed scoring metadata.
// - Generic Experiences without scoring metadata simply skip.
// =====================================================

const validateScoringConfig = (
  scoring
) => {
  if (!scoring) {
    return {
      enabled: false,
    };
  }

  if (
    scoring.votingType !== "points"
  ) {
    throw new Error(
      'Experience scoring currently requires votingType "points".'
    );
  }

  if (
    !Number.isInteger(
      scoring.maxBallotPoints
    ) ||
    scoring.maxBallotPoints < 1 ||
    scoring.maxBallotPoints > 1000
  ) {
    throw new Error(
      "Experience scoring maxBallotPoints must be a whole number from 1 through 1000."
    );
  }

  const allowedAudiences = [
    "voters",
    "judges",
    "managers",
    "admin",
  ];

  if (
    !allowedAudiences.includes(
      scoring.resultsAudience
    )
  ) {
    throw new Error(
      "Experience scoring resultsAudience is invalid."
    );
  }

  if (
    !Array.isArray(
      scoring.factors
    ) ||
    scoring.factors.length === 0
  ) {
    throw new Error(
      "Experience scoring requires at least one scoring factor."
    );
  }

  let activeFactorPoints = 0;

  scoring.factors.forEach(
    (
      factor,
      factorIndex
    ) => {
      if (
        !String(
          factor?.name || ""
        ).trim()
      ) {
        throw new Error(
          `Experience scoring factor ${factorIndex + 1} requires a name.`
        );
      }

      if (
        !Number.isFinite(
          Number(
            factor?.maxPoints
          )
        ) ||
        Number(
          factor.maxPoints
        ) <= 0
      ) {
        throw new Error(
          `Experience scoring factor "${factor.name}" requires positive maxPoints.`
        );
      }

      if (
        factor.active !== false
      ) {
        activeFactorPoints +=
          Number(
            factor.maxPoints
          );
      }

      const highlights =
        factor?.highlights || [];

      if (
        !Array.isArray(
          highlights
        )
      ) {
        throw new Error(
          `Experience scoring factor "${factor.name}" has invalid highlights.`
        );
      }

      highlights.forEach(
        (
          highlight,
          highlightIndex
        ) => {
          if (
            !String(
              highlight?.name || ""
            ).trim()
          ) {
            throw new Error(
              `Experience scoring highlight ${highlightIndex + 1} under "${factor.name}" requires a name.`
            );
          }

          if (
            !Number.isFinite(
              Number(
                highlight?.maxPoints
              )
            ) ||
            Number(
              highlight.maxPoints
            ) < 0
          ) {
            throw new Error(
              `Experience scoring highlight "${highlight.name}" has invalid maxPoints.`
            );
          }
        }
      );

      const scoredHighlightPoints =
        highlights
          .filter(
            (highlight) =>
              highlight.active !== false &&
              Number(
                highlight.maxPoints
              ) > 0
          )
          .reduce(
            (
              total,
              highlight
            ) =>
              total +
              Number(
                highlight.maxPoints
              ),
            0
          );

      if (
        scoredHighlightPoints > 0 &&
        scoredHighlightPoints !==
          Number(
            factor.maxPoints
          )
      ) {
        throw new Error(
          `Experience scoring factor "${factor.name}" highlight points must total ${factor.maxPoints}.`
        );
      }
    }
  );

  if (
    activeFactorPoints !==
    scoring.maxBallotPoints
  ) {
    throw new Error(
      `Active Experience scoring factors total ${activeFactorPoints}; expected ${scoring.maxBallotPoints}.`
    );
  }

  return {
    enabled: true,
  };
};

// =====================================================
// Build scoring configuration
// =====================================================

export const buildScoring = async ({
  eventResult,
  industryTemplate,
} = {}) => {
  const eventId =
    eventResult?.eventId || "";

  if (!eventId) {
    throw new Error(
      "Scoring Builder requires an eventId."
    );
  }

  const scoring =
    industryTemplate?.scoring || null;

  const validation =
    validateScoringConfig(
      scoring
    );

  // ===================================================
  // Generic no-op behavior
  // ===================================================
  // Car Show and other existing Experiences continue to
  // operate exactly as before unless their manifest opts
  // into a scoring configuration.
  // ===================================================

  if (!validation.enabled) {
    logger.log(
      "ℹ️ Scoring Builder skipped; Experience has no scoring template:",
      {
        value: {
          eventId,
          industry:
            industryTemplate?.key ||
            "unknown",
        },
      }
    );

    return {
      builder:
        "scoring_builder",

      ready: true,

      skipped: true,

      createdCount: 0,

      createdFactorCount: 0,

      createdHighlightCount: 0,

      message:
        "Scoring Builder skipped because this Experience has no scoring template.",
    };
  }

  logger.log(
    "🧮 Scoring Builder creating Experience scorecard:",
    {
      value: {
        eventId,
        industry:
          industryTemplate?.key ||
          "unknown",
        votingType:
          scoring.votingType,
        maxBallotPoints:
          scoring.maxBallotPoints,
        resultsAudience:
          scoring.resultsAudience,
        factorCount:
          scoring.factors.length,
      },
    }
  );

  // ===================================================
  // Build factor/highlight writes atomically.
  // ===================================================

  const batch =
    writeBatch(
      db
    );

  const createdFactors = [];

  let createdHighlightCount = 0;

  scoring.factors.forEach(
    (
      factor,
      factorIndex
    ) => {
      const factorRef =
        doc(
          collection(
            db,
            "events",
            eventId,
            "scoringFactors"
          )
        );

      const factorDoc = {
        name:
          String(
            factor.name
          ).trim(),

        maxPoints:
          Number(
            factor.maxPoints
          ),

        sortOrder:
          Number(
            factor.sortOrder ||
            factorIndex + 1
          ),

        active:
          factor.active !== false,

        demoSeed: true,

        createdAt:
          serverTimestamp(),

        updatedAt:
          serverTimestamp(),
      };

      batch.set(
        factorRef,
        factorDoc
      );

      const createdHighlights = [];

      (
        factor.highlights || []
      ).forEach(
        (
          highlight,
          highlightIndex
        ) => {
          const highlightRef =
            doc(
              collection(
                db,
                "events",
                eventId,
                "scoringFactors",
                factorRef.id,
                "highlights"
              )
            );

          const highlightDoc = {
            name:
              String(
                highlight.name
              ).trim(),

            maxPoints:
              Number(
                highlight.maxPoints ||
                0
              ),

            sortOrder:
              Number(
                highlight.sortOrder ||
                highlightIndex + 1
              ),

            active:
              highlight.active !== false,

            demoSeed: true,

            createdAt:
              serverTimestamp(),

            updatedAt:
              serverTimestamp(),
          };

          batch.set(
            highlightRef,
            highlightDoc
          );

          createdHighlightCount += 1;

          createdHighlights.push({
            id:
              highlightRef.id,

            ref:
              highlightRef,

            ...highlightDoc,
          });
        }
      );

      createdFactors.push({
        id:
          factorRef.id,

        ref:
          factorRef,

        ...factorDoc,

        highlights:
          createdHighlights,
      });
    }
  );

  await batch.commit();

  // ===================================================
  // Governed voting settings
  // ===================================================
  // Do not bypass the Integrity Engine. The existing callable
  // validates maxBallotPoints/resultsAudience and creates the
  // established immutable settings audit evidence.
  // ===================================================

  const updateVotingSettings =
    httpsCallable(
      functions,
      "updateVotingSettings"
    );

  const votingStartDate =
    new Date();

  votingStartDate.setDate(
    votingStartDate.getDate() + 21
  );


  const votingEndDate =
    new Date(
      votingStartDate
    );

  votingEndDate.setHours(
    votingEndDate.getHours() + 8
  );


  logger.log(
    "?? updateVotingSettings payload:",
    {
      value: {
        eventId,
        scoring,
        resultsAudience:
          scoring.resultsAudience,
        maxBallotPoints:
          scoring.maxBallotPoints,
      },
    }
  );

  await updateVotingSettings({
    eventId,

    votingOpen:
      false,

    votingStart:
      votingStartDate,

    votingEnd:
      votingEndDate,

    maxBallotPoints:
      scoring.maxBallotPoints,

    showLeaderboard:
      scoring.resultsAudience ===
      "voters",

    resultsAudience:
      scoring.resultsAudience,

    leaderboardLimit:
      Number(
        scoring.leaderboardLimit ||
        5
      ),

    bestInShowEnabled:
      false,

    showWinnerResultsAt:
      "",

    winnerPlaceLimit:
      3,

    showWinnerVoteCounts:
      true,

    publicEntryStart:
      null,

    publicEntryEnd:
      null,

    reason:
      "Experience Factory scoring configuration.",
  });

  logger.log(
    "✅ Scoring Builder created Experience scorecard:",
    {
      value: {
        eventId,
        factorCount:
          createdFactors.length,
        highlightCount:
          createdHighlightCount,
        maxBallotPoints:
          scoring.maxBallotPoints,
        resultsAudience:
          scoring.resultsAudience,
      },
    }
  );

  return {
    builder:
      "scoring_builder",

    ready: true,

    skipped: false,

    createdCount:
      createdFactors.length +
      createdHighlightCount,

    createdFactorCount:
      createdFactors.length,

    createdHighlightCount,

    createdFactors,

    maxBallotPoints:
      scoring.maxBallotPoints,

    resultsAudience:
      scoring.resultsAudience,

    votingType:
      scoring.votingType,

    message:
      "Scoring Builder created the Experience scorecard and voting settings.",
  };
};

// =====================================================
// Compatibility shell
// =====================================================

export const buildScoringShell = async ({
  eventResult,
  industryTemplate,
} = {}) => {
  const scoring =
    industryTemplate?.scoring ||
    null;

  if (
    !eventResult?.eventId
  ) {
    const validation =
      validateScoringConfig(
        scoring
      );

    return {
      builder:
        "scoring_builder",

      ready: false,

      skipped:
        !validation.enabled,

      plannedCount:
        validation.enabled
          ? scoring.factors.length
          : 0,

      message:
        "Scoring Builder requires an eventId before Firestore creation.",
    };
  }

  try {
    return await buildScoring({
      eventResult,
      industryTemplate,
    });
  } catch (err) {
    logger.error(
      "? Scoring Builder diagnostic failure:",
      {
        value: {
          message:
            err?.message ||
            "No error message",

          stack:
            err?.stack ||
            "No stack",

          scoring:
            industryTemplate?.scoring,

          industry:
            industryTemplate?.key,
        },
      }
    );

    throw err;
  }
};